import React, { useEffect, useState, useMemo, useRef } from 'react'
import { Typography, Table, Card, Tag, Space, message, Spin, Button, Modal, Form, Input, DatePicker, Select, Calendar, Badge, Row, Col, Statistic, Collapse, AutoComplete, Tooltip, Checkbox } from 'antd'
import { ReloadOutlined, CalendarOutlined, TeamOutlined, CheckCircleOutlined, PlusOutlined, EyeOutlined, BarChartOutlined, SendOutlined, ClockCircleOutlined, MailOutlined, SyncOutlined, UserOutlined, SearchOutlined } from '@ant-design/icons'
import { apiService } from '../services/apiService'
import dayjs from 'dayjs'
import { useAppSelector } from '../hooks/redux'
import ScheduleStatusCell, { getScheduleStatus } from './ScheduleStatusCell'

const { Title, Text } = Typography

interface MarketingSchedule {
  id: number
  sprint_number: string
  number_of_leads: number | null
  scheduled_datetime: string
  date_1: string
  date_2: string | null
  date_3: string | null
  date_4: string | null
  date_5: string | null
  batch: string
  success_status: string | null
  unique_emails: number | null
  unique_leads: number | null
  unique_email_clicks: number | null
  unique_lead_clicks: number | null
  email_click_rate: number | null
  lead_click_rate: number | null
  extra_data?: any
}

interface CadenceAnalytics {
  id: number
  sprint_number: string
  batch_type: string
  total_leads: number
  frequency: number
  emails_sent: number
  times_scheduled: number
  paying_customers_count: number
  first_demos_count: number
  last_sent_date: string | null
  next_scheduled_date: string | null
  success_status: string | null
  scheduled_datetime: string
  campaign_dates: {
    date_1: string | null
    date_2: string | null
    date_3: string | null
    date_4: string | null
    date_5: string | null
  }
}

function Marketing() {
  const [schedules, setSchedules] = useState<MarketingSchedule[]>([])
  const [analytics, setAnalytics] = useState<CadenceAnalytics[]>([])
  const [analyticsSummary, setAnalyticsSummary] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [analyticsLoading, setAnalyticsLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [form] = Form.useForm()
  const [selectedBatch, setSelectedBatch] = useState<string | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewData, setPreviewData] = useState<any>(null)
  const [isSprintModalOpen, setIsSprintModalOpen] = useState(false)
  const [selectedSprint, setSelectedSprint] = useState<string | null>(null)
  const [sprintDetails, setSprintDetails] = useState<any[]>([])
  const [sprintDetailsLoading, setSprintDetailsLoading] = useState(false)
  const [calendarMode, setCalendarMode] = useState<'month' | 'year'>('month')
  const [emailAnalytics, setEmailAnalytics] = useState<any>(null)
  const [emailAnalyticsLoading, setEmailAnalyticsLoading] = useState(false)
  const [selectedEmailDate, setSelectedEmailDate] = useState<any>(null)
  const [selectedEmailSprint, setSelectedEmailSprint] = useState<string>('')
  const [availableSprints, setAvailableSprints] = useState<string[]>([])
  const [sprintsLoading, setSprintsLoading] = useState(false)
  const [marketingSearchText, setMarketingSearchText] = useState<string>('')
  const [analyticsSearchText, setAnalyticsSearchText] = useState<string>('')
  const [marketingSearchInput, setMarketingSearchInput] = useState<string>('')
  const [analyticsSearchInput, setAnalyticsSearchInput] = useState<string>('')
  const [sprintOptions, setSprintOptions] = useState<any[]>([])
  const [sprintSearchLoading, setSprintSearchLoading] = useState(false)
  const [selectedSprintNumbers, setSelectedSprintNumbers] = useState<string[]>([])
  const [sprintSearchInput, setSprintSearchInput] = useState<string>('')
  const [sprintSearchText, setSprintSearchText] = useState<string>('')
  const [blacklistedSprintOptions, setBlacklistedSprintOptions] = useState<any[]>([])
  const [blacklistedSprintSearchLoading, setBlacklistedSprintSearchLoading] = useState(false)
  const [blacklistedSprintSearchInput, setBlacklistedSprintSearchInput] = useState<string>('')
  const [blacklistedSprintSearchText, setBlacklistedSprintSearchText] = useState<string>('')
  const { user } = useAppSelector((state) => state.auth)
  const unrestrictedCalendarEmails = new Set([
    'jisan@primenumbers.in',
    'sneha@primenumbers.in',
  ])
  const isCalendarUnrestricted =
    !!user?.email && unrestrictedCalendarEmails.has(user.email.toLowerCase())

  // Ref for email analytics section
  const emailAnalyticsRef = useRef<HTMLDivElement>(null)

  // Memoized filtered data
  const filteredSchedules = useMemo(() =>
    schedules.filter(schedule =>
      schedule.sprint_number.toLowerCase().includes(marketingSearchText.toLowerCase())
    ), [schedules, marketingSearchText]
  )

  const filteredAnalytics = useMemo(() =>
    analytics.filter(analytic =>
      analytic.sprint_number.toLowerCase().includes(analyticsSearchText.toLowerCase())
    ), [analytics, analyticsSearchText]
  )

  const displayStats = useMemo(() => {
    if (!analyticsSummary) return null

    if (analyticsSearchText) {
      return {
        total_campaigns: filteredAnalytics.length,
        total_leads: filteredAnalytics.reduce((sum, a) => sum + a.total_leads, 0),
        active_campaigns: filteredAnalytics.filter(a =>
          a.next_scheduled_date && dayjs(a.next_scheduled_date).isAfter(dayjs())
        ).length,
        completed_campaigns: filteredAnalytics.filter(a =>
          !a.next_scheduled_date ||
          (a.success_status && a.success_status.toLowerCase().includes('success'))
        ).length
      }
    }
    return analyticsSummary
  }, [analyticsSummary, analyticsSearchText, filteredAnalytics])

  useEffect(() => {
    fetchSchedules()
    fetchAnalytics()
  }, [])

  // Debounce search inputs
  useEffect(() => {
    const timer = setTimeout(() => {
      setMarketingSearchText(marketingSearchInput)
    }, 300)

    return () => clearTimeout(timer)
  }, [marketingSearchInput])

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnalyticsSearchText(analyticsSearchInput)
    }, 300)

    return () => clearTimeout(timer)
  }, [analyticsSearchInput])

  // Debounce sprint search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSprintSearchText(sprintSearchInput)
    }, 500)

    return () => clearTimeout(timer)
  }, [sprintSearchInput])

  useEffect(() => {
    const timer = setTimeout(() => {
      setBlacklistedSprintSearchText(blacklistedSprintSearchInput)
    }, 500)

    return () => clearTimeout(timer)
  }, [blacklistedSprintSearchInput])

  // Trigger search when debounced text changes
  useEffect(() => {
    if (sprintSearchText) {
      handleSprintSearch(sprintSearchText, setSprintOptions, setSprintSearchLoading)
    } else {
      setSprintOptions([])
    }
  }, [sprintSearchText])

  useEffect(() => {
    if (blacklistedSprintSearchText) {
      handleSprintSearch(
        blacklistedSprintSearchText,
        setBlacklistedSprintOptions,
        setBlacklistedSprintSearchLoading
      )
    } else {
      setBlacklistedSprintOptions([])
    }
  }, [blacklistedSprintSearchText])

  const fetchSchedules = async () => {
    setLoading(true)
    try {
      const response = await apiService.get('/marketing/schedule/')
      if (response.data.success) {
        setSchedules(response.data.data)
      }
    } catch (error) {
      console.error('Error fetching marketing schedules:', error)
      message.error('Failed to load marketing schedules')
    } finally {
      setLoading(false)
    }
  }

  const fetchAnalytics = async () => {
    setAnalyticsLoading(true)
    try {
      const response = await apiService.get('/marketing/cadence-analytics/')
      if (response.data.success) {
        setAnalytics(response.data.data)
        setAnalyticsSummary(response.data.summary)
      }
    } catch (error) {
      console.error('Error fetching cadence analytics:', error)
      message.error('Failed to load cadence analytics')
    } finally {
      setAnalyticsLoading(false)
    }
  }

  const handleRefresh = async () => {
    setRefreshing(true)
    try {
      const [scheduleResponse, analyticsResponse] = await Promise.all([
        apiService.get('/marketing/schedule/'),
        apiService.get('/marketing/cadence-analytics/')
      ])
      
      if (scheduleResponse.data.success) {
        setSchedules(scheduleResponse.data.data)
      }
      if (analyticsResponse.data.success) {
        setAnalytics(analyticsResponse.data.data)
        setAnalyticsSummary(analyticsResponse.data.summary)
      }
      
      message.success('Data refreshed successfully')
    } catch (error) {
      console.error('Error refreshing data:', error)
      message.error('Failed to refresh data')
    } finally {
      setRefreshing(false)
    }
  }

  const handleCreateEntry = async (values: any) => {
    setCreating(true)
    try {
      const sprintNumbers = values.sprint_numbers || []

      if (sprintNumbers.length === 0) {
        message.error('Please select at least one sprint number')
        setCreating(false)
        return
      }

      // Create individual entries for each sprint number
      const createPromises = sprintNumbers.map((sprintNumber: string) => {
        const formData = {
          sprint_number: sprintNumber,
          blacklisted_sprint_numbers: values.blacklisted_sprint_numbers || [],
          batch: values.batch,
          date_1: values.date_1.format('YYYY-MM-DD'),
          date_2: values.date_2 ? values.date_2.format('YYYY-MM-DD') : null,
          date_3: values.date_3 ? values.date_3.format('YYYY-MM-DD') : null,
          date_4: values.batch === 'plus mail cadence' ? values.date_4.format('YYYY-MM-DD') : null,
          date_5: values.batch === 'plus mail cadence' ? values.date_5.format('YYYY-MM-DD') : null,
          include_paying_customers: !!values.include_paying_customers,
        }
        return apiService.post('/marketing/schedule/create/', formData)
      })

      const responses = await Promise.all(createPromises)

      // Check if all succeeded
      const allSucceeded = responses.every(response => response.data.success)

      if (allSucceeded) {
        message.success(`Successfully created ${sprintNumbers.length} marketing schedule${sprintNumbers.length > 1 ? 's' : ''}`)
        setIsModalOpen(false)
        form.resetFields()
        await Promise.all([fetchSchedules(), fetchAnalytics()]) // Refresh both lists
      } else {
        const failedCount = responses.filter(r => !r.data.success).length
        message.warning(`${responses.length - failedCount} succeeded, ${failedCount} failed`)
      }
    } catch (error: any) {
      console.error('Error creating marketing schedule:', error)
      const errorMsg = error.response?.data?.error || 'Failed to create marketing schedule'
      message.error(errorMsg)
    } finally {
      setCreating(false)
    }
  }

  const handleModalCancel = () => {
    setIsModalOpen(false)
    form.resetFields()
    setSelectedBatch(null)
    setPreviewData(null)
    setSelectedSprintNumbers([])
    setSprintOptions([])
    setSprintSearchInput('')
    setSprintSearchText('')
    setBlacklistedSprintOptions([])
    setBlacklistedSprintSearchInput('')
    setBlacklistedSprintSearchText('')
  }

  const handleBatchChange = (value: string) => {
    setSelectedBatch(value)
    // Clear date fields when batch changes
    form.setFieldsValue({
      date_2: null,
      date_3: null,
      date_4: null,
      date_5: null,
      include_paying_customers: false,
    })
    // Clear preview data when batch changes
    setPreviewData(null)
  }

  const handlePreviewSprint = async () => {
    const sprintNumber = form.getFieldValue('sprint_number')
    if (!sprintNumber) {
      message.error('Please enter a sprint number first')
      return
    }

    setPreviewLoading(true)
    try {
      const response = await apiService.previewSprintLeads(sprintNumber)
      if (response.success) {
        setPreviewData(response)
        message.success(response.message)
      }
    } catch (error: any) {
      console.error('Error previewing sprint leads:', error)
      const errorMsg = error.response?.data?.error || 'Failed to preview sprint leads'
      message.error(errorMsg)
    } finally {
      setPreviewLoading(false)
    }
  }

  const handleSprintClick = async (sprintNumber: string) => {
    setSelectedSprint(sprintNumber)
    setIsSprintModalOpen(true)
    setSprintDetailsLoading(true)
    
    try {
      // Fetch all schedules for this sprint
      const response = await apiService.get('/marketing/schedule/')
      if (response.data.success) {
        const sprintSchedules = response.data.data.filter(
          (schedule: any) => schedule.sprint_number === sprintNumber
        )
        setSprintDetails(sprintSchedules)
      }
    } catch (error: any) {
      console.error('Error fetching sprint details:', error)
      message.error('Failed to load sprint details')
    } finally {
      setSprintDetailsLoading(false)
    }
  }

  const handleSprintModalClose = () => {
    setIsSprintModalOpen(false)
    setSelectedSprint(null)
    setSprintDetails([])
  }

  const handleEmailDateChange = async (date: any) => {
    if (!date) {
      setSelectedEmailDate(null)
      setAvailableSprints([])
      setSelectedEmailSprint('')
      setEmailAnalytics(null)
      return
    }

    setSelectedEmailDate(date)
    setSelectedEmailSprint('')
    setEmailAnalytics(null)
    setSprintsLoading(true)

    try {
      const formattedDate = date.format('YYYY-MM-DD')
      const response = await apiService.getSprintsForDate(formattedDate)

      if (response.success) {
        setAvailableSprints(response.sprint_numbers || [])
        if (response.sprint_numbers && response.sprint_numbers.length === 0) {
          message.info('No sprints found for the selected date')
        }
      } else {
        message.error(response.error || 'Failed to load sprints for date')
        setAvailableSprints([])
      }
    } catch (error: any) {
      console.error('Error fetching sprints for date:', error)
      message.error('Failed to load sprints for date')
      setAvailableSprints([])
    } finally {
      setSprintsLoading(false)
    }
  }

  const handleEmailSearch = async () => {
    if (!selectedEmailDate) {
      message.error('Please select a date')
      return
    }

    if (!selectedEmailSprint) {
      message.error('Please enter a sprint number')
      return
    }

    setEmailAnalyticsLoading(true)

    try {
      const formattedDate = selectedEmailDate.format('YYYY-MM-DD')
      const response = await apiService.getEmailAnalytics(formattedDate, selectedEmailSprint)

      if (response.success) {
        setEmailAnalytics(response)
      } else {
        message.error(response.error || 'Failed to load email analytics')
        setEmailAnalytics(null)
      }
    } catch (error: any) {
      console.error('Error fetching email analytics:', error)
      message.error('Failed to load email analytics')
      setEmailAnalytics(null)
    } finally {
      setEmailAnalyticsLoading(false)
    }
  }

  // Future date validator
  const validateFutureDate = (_: any, value: any) => {
    if (!value) return Promise.resolve()
    if (isCalendarUnrestricted) {
      if (value.isBefore(dayjs().startOf('day'))) {
        return Promise.reject(new Error('Date must be today or later'))
      }
      return Promise.resolve()
    }
    const now = dayjs()
    if (value.isBefore(now)) {
      return Promise.reject(new Error('Date must be in the future'))
    }
    return Promise.resolve()
  }

  const isScheduleDateDisabled = (current: dayjs.Dayjs) => {
    if (!current) return false
    if (isCalendarUnrestricted) {
      return current < dayjs().startOf('day')
    }
    return current < dayjs().add(3, 'day').startOf('day')
  }

  const isEmailAnalyticsDateDisabled = (current: dayjs.Dayjs) => {
    if (!current) return false
    return current >= dayjs().startOf('day')
  }

  // Custom date renderer with tooltip for disabled dates
  const dateRenderWithTooltip = (current: dayjs.Dayjs) => {
    const isDisabled = current && !isCalendarUnrestricted && current < dayjs().add(3, 'day').startOf('day')
    const dateNum = current.date()

    if (isDisabled) {
      return (
        <Tooltip title="need to schedule at least 3 days before" placement="top">
          <div className="ant-picker-cell-inner">{dateNum}</div>
        </Tooltip>
      )
    }

    return <div className="ant-picker-cell-inner">{dateNum}</div>
  }

  const handleSprintSearch = async (
    searchValue: string,
    setOptions: React.Dispatch<React.SetStateAction<any[]>>,
    setLoading: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    if (!searchValue || searchValue.length < 2) {
      setOptions([])
      return
    }

    setLoading(true)
    try {
      const response = await apiService.searchSprintNumbers(searchValue)
      if (response.success && response.sprint_numbers) {
        setOptions(response.sprint_numbers)
      } else {
        setOptions([])
      }
    } catch (error: any) {
      console.error('Error searching sprint numbers:', error)
      setOptions([])
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getExtraDataTooltipContent = (extraData: any) => {
    if (!extraData || (typeof extraData === 'object' && Object.keys(extraData).length === 0)) return null

    const formatValue = (value: any) => {
      if (value === null || value === undefined) return '-'
      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value)
      return JSON.stringify(value)
    }
    const formatKey = (key: string) => key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())
    const title = <Text strong style={{ display: 'block', marginBottom: 10, color: '#0f172a' }}>Extra Data</Text>
    const listStyle = { margin: 0, paddingLeft: 18, listStyleType: 'disc' as const }

    if (typeof extraData === 'object') {
      return (
        <div style={{ maxWidth: 460 }}>
          {title}
          <ul style={listStyle}>
            {Object.entries(extraData).map(([key, value]) => (
              <li key={key} style={{ padding: '4px 0' }}>
                <Tag color="blue" style={{ marginRight: 6 }}>{formatKey(key)}</Tag>
                <Tag color="volcano" style={{ marginRight: 0 }}>{formatValue(value)}</Tag>
              </li>
            ))}
          </ul>
        </div>
      )
    }

    return (
      <div style={{ maxWidth: 360 }}>
        <Text>{formatValue(extraData)}</Text>
      </div>
    )
  }

  // Calendar data cell renderer for month view
  const dateCellRender = (value: dayjs.Dayjs) => {
    const dateStr = value.format('YYYY-MM-DD')
    const campaignsOnDate = schedules.filter(schedule => {
      const date1 = schedule.date_1 && dayjs(schedule.date_1).format('YYYY-MM-DD') === dateStr
      const date2 = schedule.date_2 && dayjs(schedule.date_2).format('YYYY-MM-DD') === dateStr
      const date3 = schedule.date_3 && dayjs(schedule.date_3).format('YYYY-MM-DD') === dateStr
      return date1 || date2 || date3 || [schedule.date_4, schedule.date_5].some(d => d && dayjs(d).format('YYYY-MM-DD') === dateStr)
    })

    if (campaignsOnDate.length === 0) return null

    return (
      <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
        {campaignsOnDate.map(campaign => {
          const getCampaignType = () => {
            const date1 = campaign.date_1 && dayjs(campaign.date_1).format('YYYY-MM-DD') === dateStr
            const date2 = campaign.date_2 && dayjs(campaign.date_2).format('YYYY-MM-DD') === dateStr
            const date3 = campaign.date_3 && dayjs(campaign.date_3).format('YYYY-MM-DD') === dateStr
            
            if (date1) {
              if (['3 mail cadence', 'plus mail cadence'].includes(campaign.batch)) return 'Mail 1'
              if (campaign.batch === 'missed bids') return 'Missed Bids'
              if (campaign.batch === 'lost bids') return 'Lost Bids'
              if (campaign.batch === 'pricing_mail') return 'Pricing Mail'
            }
            if (date2) return 'Mail 2'
            if (date3) return 'Mail 3'
            if (campaign.date_4 && dayjs(campaign.date_4).format('YYYY-MM-DD') === dateStr) return 'Mail 4'
            if (campaign.date_5 && dayjs(campaign.date_5).format('YYYY-MM-DD') === dateStr) return 'Mail 5'
            return campaign.batch
          }

          return (
            <li key={campaign.id} style={{ marginBottom: 2 }}>
              <Badge 
                status={getScheduleStatus(campaign).color}
                text={
                  <span style={{ fontSize: '12px' }}>
                    {campaign.sprint_number} - {getCampaignType()}
                  </span>
                } 
              />
            </li>
          )
        })}
      </ul>
    )
  }

  // Calendar month cell renderer for year view
  const monthCellRender = (value: dayjs.Dayjs) => {
    const monthStr = value.format('YYYY-MM')
    
    // Get all campaigns in this month
    const campaignsInMonth = schedules.filter(schedule => {
      const checkDate = (dateStr: string | null) => {
        if (!dateStr) return false
        return dayjs(dateStr).format('YYYY-MM') === monthStr
      }
      return checkDate(schedule.date_1) || checkDate(schedule.date_2) || checkDate(schedule.date_3) || checkDate(schedule.date_4) || checkDate(schedule.date_5)
    })

    if (campaignsInMonth.length === 0) return null

    // Group campaigns by sprint number
    const sprintMap = new Map<string, number>()
    campaignsInMonth.forEach(campaign => {
      sprintMap.set(campaign.sprint_number, (sprintMap.get(campaign.sprint_number) || 0) + 1)
    })

    return (
      <div style={{ padding: '8px' }}>
        <div style={{ marginBottom: 8 }}>
          <Text strong style={{ fontSize: '14px' }}>
            {campaignsInMonth.length} Campaign{campaignsInMonth.length !== 1 ? 's' : ''}
          </Text>
        </div>
        <ul style={{ margin: 0, padding: 0, listStyle: 'none', fontSize: '12px' }}>
          {Array.from(sprintMap.entries()).slice(0, 3).map(([sprint, count]) => (
            <li key={sprint} style={{ marginBottom: 2 }}>
              <Badge status="processing" text={`${sprint} (${count})`} />
            </li>
          ))}
          {sprintMap.size > 3 && (
            <li style={{ marginTop: 4 }}>
              <Text type="secondary" style={{ fontSize: '11px' }}>
                +{sprintMap.size - 3} more sprint{sprintMap.size - 3 !== 1 ? 's' : ''}
              </Text>
            </li>
          )}
        </ul>
      </div>
    )
  }

  // Handle calendar mode change
  const handleCalendarModeChange = (value: dayjs.Dayjs, mode: 'month' | 'year') => {
    setCalendarMode(mode)
  }

  // Handle campaign date click - scroll to email analytics and populate fields
  const handleCampaignDateClick = async (date: string, sprintNumber: string) => {
    const dateObj = dayjs(date)

    // Scroll to email analytics section
    emailAnalyticsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })

    // Set the date
    setSelectedEmailDate(dateObj)
    setEmailAnalytics(null)
    setSprintsLoading(true)

    try {
      // Fetch available sprints for the selected date
      const formattedDate = dateObj.format('YYYY-MM-DD')
      const response = await apiService.getSprintsForDate(formattedDate)

      if (response.success) {
        setAvailableSprints(response.sprint_numbers || [])

        // Auto-select the sprint if available
        if (response.sprint_numbers && response.sprint_numbers.includes(sprintNumber)) {
          setSelectedEmailSprint(sprintNumber)

          // Wait for sprint loading to finish, then trigger search
          setTimeout(async () => {
            setEmailAnalyticsLoading(true)
            try {
              const analyticsResponse = await apiService.getEmailAnalytics(formattedDate, sprintNumber)

              if (analyticsResponse.success) {
                setEmailAnalytics(analyticsResponse)
                message.success(`Loaded email analytics for ${sprintNumber} on ${dateObj.format('MMM DD, YYYY')}`)
              } else {
                message.error(analyticsResponse.error || 'Failed to load email analytics')
                setEmailAnalytics(null)
              }
            } catch (error: any) {
              console.error('Error fetching email analytics:', error)
              message.error('Failed to load email analytics')
              setEmailAnalytics(null)
            } finally {
              setEmailAnalyticsLoading(false)
            }
          }, 500)
        } else {
          message.warning(`Sprint ${sprintNumber} not found for ${dateObj.format('MMM DD, YYYY')}`)
        }
      } else {
        message.error(response.error || 'Failed to load sprints for date')
        setAvailableSprints([])
      }
    } catch (error: any) {
      console.error('Error fetching sprints for date:', error)
      message.error('Failed to load sprints for date')
      setAvailableSprints([])
    } finally {
      setSprintsLoading(false)
    }
  }

  const columns = [
    {
      title: 'Sprint',
      dataIndex: 'sprint_number',
      key: 'sprint_number',
       width: 160,
      render: (text: string, record: MarketingSchedule) => {
        const tooltipContent = getExtraDataTooltipContent(record.extra_data)
        if (!tooltipContent) {
          return <Text strong>{text}</Text>
        }
        return (
          <Tooltip
            placement="top"
            title={
              <div
                style={{
                  background: '#ffffff',
                  color: '#111827',
                  border: '1px solid #e5e7eb',
                  borderRadius: 10,
                  padding: 14,
                  boxShadow: '0 12px 32px rgba(15,23,42,0.18)',
                }}
              >
                {tooltipContent}
              </div>
            }
            overlayInnerStyle={{ padding: 0, background: 'transparent', boxShadow: 'none' }}
          >
            <Text strong style={{ cursor: 'help', textDecoration: 'underline dotted' }}>{text}</Text>
          </Tooltip>
        )
      },
    },
    {
      title: 'Batch',
      dataIndex: 'batch',
      key: 'batch',
      width: 110,
      render: (batch: string) => (
        <Tag color="blue">{batch}</Tag>
      ),
    },
    {
      title: 'Number of Leads',
      dataIndex: 'number_of_leads',
      key: 'number_of_leads',
      width: 120,
      render: (count: number | null) => (
        <Space>
          <TeamOutlined />
          <Text>{count || 0}</Text>
        </Space>
      ),
      sorter: (a: MarketingSchedule, b: MarketingSchedule) => (a.number_of_leads || 0) - (b.number_of_leads || 0),
    },
    {
      title: 'Scheduled Date',
      dataIndex: 'scheduled_datetime',
      key: 'scheduled_datetime',
      width: 170,
      render: (date: string) => (
        <Space>
          <CalendarOutlined />
          <Text>{formatDate(date)}</Text>
        </Space>
      ),
      sorter: (a: MarketingSchedule, b: MarketingSchedule) => new Date(a.scheduled_datetime).getTime() - new Date(b.scheduled_datetime).getTime(),
      filters: (() => {
        // Create unique month-year filters from the schedules
        const monthYearSet = new Set<string>()
        schedules.forEach(schedule => {
          if (schedule.scheduled_datetime) {
            const date = new Date(schedule.scheduled_datetime)
            const monthYear = `${date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`
            monthYearSet.add(monthYear)
          }
        })

        // Convert to filter array and sort by date
        return Array.from(monthYearSet)
          .map(monthYear => ({
            text: monthYear,
            value: monthYear,
          }))
          .sort((a, b) => {
            const dateA = new Date(a.value)
            const dateB = new Date(b.value)
            return dateB.getTime() - dateA.getTime()
          })
      })(),
      onFilter: (value: boolean | React.Key, record: MarketingSchedule) => {
        const recordDate = new Date(record.scheduled_datetime)
        const recordMonthYear = `${recordDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`
        return recordMonthYear === value
      },
    },
    {
      title: 'Campaign Dates',
      key: 'campaign_dates',
      width: 210,
      render: (record: MarketingSchedule) => (
        <Space direction="vertical" size={0}>
          <Button
            type="link"
            size="small"
            style={{ padding: 0, height: 'auto', color: '#1890ff' }}
            onClick={() => handleCampaignDateClick(record.date_1, record.sprint_number)}
          >
            Date 1: {formatDate(record.date_1)}
          </Button>
          {record.date_2 && (
            <Button
              type="link"
              size="small"
              style={{ padding: 0, height: 'auto', color: '#1890ff' }}
              onClick={() => handleCampaignDateClick(record.date_2!, record.sprint_number)}
            >
              Date 2: {formatDate(record.date_2)}
            </Button>
          )}
          {record.date_3 && (
            <Button
              type="link"
              size="small"
              style={{ padding: 0, height: 'auto', color: '#1890ff' }}
              onClick={() => handleCampaignDateClick(record.date_3!, record.sprint_number)}
            >
              Date 3: {formatDate(record.date_3)}
            </Button>
          )}
          {record.date_4 && (
            <Button
              type="link"
              size="small"
              style={{ padding: 0, height: 'auto', color: '#1890ff' }}
              onClick={() => handleCampaignDateClick(record.date_4!, record.sprint_number)}
            >
              Date 4: {formatDate(record.date_4)}
            </Button>
          )}
          {record.date_5 && (
            <Button
              type="link"
              size="small"
              style={{ padding: 0, height: 'auto', color: '#1890ff' }}
              onClick={() => handleCampaignDateClick(record.date_5!, record.sprint_number)}
            >
              Date 5: {formatDate(record.date_5)}
            </Button>
          )}
        </Space>
      ),
    },
    {
      title: 'Emails / Clicks',
      key: 'emails_combined',
      width: 130,
      render: (record: MarketingSchedule) => (
        <Text>
          {record.unique_emails ?? '-'} / {record.unique_email_clicks ?? '-'}
        </Text>
      ),
      sorter: (a: MarketingSchedule, b: MarketingSchedule) => (a.unique_emails || 0) - (b.unique_emails || 0),
    },
    {
      title: 'Leads / Clicks',
      key: 'leads_combined',
      width: 130,
      render: (record: MarketingSchedule) => (
        <Text>
          {record.unique_leads ?? '-'} / {record.unique_lead_clicks ?? '-'}
        </Text>
      ),
      sorter: (a: MarketingSchedule, b: MarketingSchedule) => (a.unique_leads || 0) - (b.unique_leads || 0),
    },
    {
      title: 'Status',
      key: 'status',
      width: 140,
      filters: [
        { text: 'Scheduled', value: 'Scheduled' },
        { text: 'Sent', value: 'Sent' },
        { text: 'Failed', value: 'Failed' },
      ],
      onFilter: (value: any, record: MarketingSchedule) =>
        getScheduleStatus(record).label === value,
      render: (record: MarketingSchedule) => <ScheduleStatusCell record={record} />,
    },
  ]

  return (
    <div style={{ padding: '0 8px' }}>
      <Card
        title={
          <Space wrap>
            <Title level={4} style={{ margin: 0, fontSize: 'clamp(16px, 4vw, 20px)' }}>Marketing Run Schedule</Title>
            <Text type="secondary">({schedules.length} campaigns)</Text>
          </Space>
        }
        extra={
          <Space wrap size="small" style={{ maxWidth: '100%' }}>
            <Input
              placeholder="Search by sprint number..."
              prefix={<SearchOutlined />}
              value={marketingSearchInput}
              onChange={(e) => setMarketingSearchInput(e.target.value)}
              style={{ width: '100%', minWidth: 150, maxWidth: 200 }}
              allowClear
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIsModalOpen(true)}
              style={{ minWidth: 'auto' }}
            >
              <span style={{ display: 'inline-block' }}>Add Entry</span>
            </Button>
            <Button
              icon={<ReloadOutlined spin={refreshing} />}
              onClick={handleRefresh}
              loading={refreshing}
            >
              Refresh
            </Button>
          </Space>
        }
        style={{ marginTop: 16 }}
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: '50px 0' }}>
            <Spin size="large" />
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={filteredSchedules}
            rowKey="id"
            pagination={{
              position: ['topRight', 'bottomRight'],
              defaultPageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} schedules`,
            }}
            scroll={{ x: 1030 }}
          />
        )}
      </Card>

      {/* Calendar View - Collapsible */}
      <Card style={{ marginTop: 16 }}>
        <Collapse
          defaultActiveKey={[]} // Empty array means collapsed by default
          items={[
            {
              key: 'calendar',
              label: (
                <Space>
                  <CalendarOutlined />
                  <Title level={4} style={{ margin: 0 }}>Campaign Calendar</Title>
                  <Text type="secondary">Visual timeline of all marketing campaigns</Text>
                </Space>
              ),
              children: loading ? (
                <div style={{ textAlign: 'center', padding: '50px 0' }}>
                  <Spin size="large" />
                </div>
              ) : (
                <>
                  {/* Legend */}
                  <div style={{ marginBottom: 16, padding: '12px', backgroundColor: '#fafafa', borderRadius: '6px' }}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>Legend:</Text>
                    <Space wrap size="small">
                      <Badge status="success" text="Scheduled/Completed" />
                      <Badge status="error" text="Failed" />
                      <Badge status="processing" text="In Progress" />
                      <Badge status="default" text="Pending" />
                    </Space>
                  </div>

                  <Calendar
                    dateCellRender={dateCellRender}
                    monthCellRender={monthCellRender}
                    mode={calendarMode}
                    onPanelChange={handleCalendarModeChange}
                  />
                </>
              )
            }
          ]}
        />
      </Card>

      {/* Cadence Analytics Section */}
      <Card
        title={
          <Space wrap>
            <BarChartOutlined />
            <Title level={4} style={{ margin: 0, fontSize: 'clamp(16px, 4vw, 20px)' }}>Cadence Analytics</Title>
            <Text type="secondary" style={{ fontSize: 'clamp(12px, 3vw, 14px)' }}>Sprint numbers, leads count, and campaign tracking</Text>
          </Space>
        }
        extra={
          <Input
            placeholder="Search by sprint number..."
            prefix={<SearchOutlined />}
            value={analyticsSearchInput}
            onChange={(e) => setAnalyticsSearchInput(e.target.value)}
            style={{ width: '100%', minWidth: 150, maxWidth: 200 }}
            allowClear
          />
        }
        style={{ marginTop: 16 }}
      >
        {analyticsLoading ? (
          <div style={{ textAlign: 'center', padding: '50px 0' }}>
            <Spin size="large" />
          </div>
        ) : (
          <>
            {/* Summary Statistics */}
            {displayStats && (
              <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} md={6}>
                  <Card size="small">
                    <Statistic
                      title="Total Campaigns"
                      value={displayStats.total_campaigns}
                      prefix={<MailOutlined />}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Card size="small">
                    <Statistic
                      title="Total Leads"
                      value={displayStats.total_leads}
                      prefix={<TeamOutlined />}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Card size="small">
                    <Statistic
                      title="Active Campaigns"
                      value={displayStats.active_campaigns}
                      prefix={<ClockCircleOutlined />}
                      valueStyle={{ color: '#1890ff' }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Card size="small">
                    <Statistic
                      title="Completed"
                      value={displayStats.completed_campaigns}
                      prefix={<CheckCircleOutlined />}
                      valueStyle={{ color: '#52c41a' }}
                    />
                  </Card>
                </Col>
              </Row>
            )}

            {/* Analytics Table */}
            <Table
              dataSource={filteredAnalytics}
              rowKey="id"
              pagination={{
                position: ['topRight', 'bottomRight'],
                defaultPageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} campaigns`,
              }}
              columns={[
                {
                  title: 'Sprint Number',
                  dataIndex: 'sprint_number',
                  key: 'sprint_number',
                  width: 140,
                  render: (text: string) => (
                    <Button 
                      type="link" 
                      style={{ padding: 0, fontWeight: 'bold', color: '#1890ff' }}
                      onClick={() => handleSprintClick(text)}
                    >
                      {text}
                    </Button>
                  ),
                  sorter: (a, b) => a.sprint_number.localeCompare(b.sprint_number),
                },
                {
                  title: 'Type',
                  dataIndex: 'batch_type',
                  key: 'batch_type',
                  width: 120,
                  render: (type: string) => (
                    <Tag color={['3 mail cadence', 'plus mail cadence'].includes(type) ? 'blue' : 'green'}>
                      {type}
                    </Tag>
                  ),
                  filters: [
                    { text: '3 mail cadence', value: '3 mail cadence' },
                    { text: 'plus mail cadence', value: 'plus mail cadence' },
                    { text: 'missed bids', value: 'missed bids' },
                    { text: 'lost bids', value: 'lost bids' },
                    { text: 'pricing_mail', value: 'pricing_mail' },
                  ],
                  onFilter: (value, record) => record.batch_type === value,
                },
                {
                  title: 'Leads',
                  dataIndex: 'total_leads',
                  key: 'total_leads',
                  width: 80,
                  render: (count: number) => (
                    <Space>
                      <TeamOutlined />
                      <Text>{count}</Text>
                    </Space>
                  ),
                  sorter: (a, b) => a.total_leads - b.total_leads,
                },
                {
                  title: 'Times Scheduled',
                  dataIndex: 'times_scheduled',
                  key: 'times_scheduled',
                  width: 120,
                  render: (times: number) => (
                    <Space>
                      <SyncOutlined />
                      <Text>{times} time{times !== 1 ? 's' : ''}</Text>
                    </Space>
                  ),
                  sorter: (a, b) => a.times_scheduled - b.times_scheduled,
                },
                {
                  title: 'Paying Customers',
                  dataIndex: 'paying_customers_count',
                  key: 'paying_customers_count',
                  width: 120,
                  render: (count: number) => (
                    <Space>
                      <UserOutlined />
                      <Text style={{ color: count > 0 ? '#52c41a' : '#d9d9d9' }}>
                        {count}
                      </Text>
                    </Space>
                  ),
                  sorter: (a, b) => a.paying_customers_count - b.paying_customers_count,
                },
                {
                  title: 'First Demos',
                  dataIndex: 'first_demos_count',
                  key: 'first_demos_count',
                  width: 100,
                  render: (count: number) => (
                    <Space>
                      <CheckCircleOutlined />
                      <Text style={{ color: count > 0 ? '#1890ff' : '#d9d9d9' }}>
                        {count}
                      </Text>
                    </Space>
                  ),
                  sorter: (a, b) => a.first_demos_count - b.first_demos_count,
                },
                {
                  title: 'Last Sent',
                  dataIndex: 'last_sent_date',
                  key: 'last_sent_date',
                  width: 130,
                  render: (date: string | null) => (
                    <Space>
                      <ClockCircleOutlined />
                      {date ? (
                        <Text>{dayjs(date).format('MMM DD, YYYY')}</Text>
                      ) : (
                        <Text type="secondary">Not sent</Text>
                      )}
                    </Space>
                  ),
                  sorter: (a, b) => {
                    if (!a.last_sent_date && !b.last_sent_date) return 0
                    if (!a.last_sent_date) return 1
                    if (!b.last_sent_date) return -1
                    return dayjs(a.last_sent_date).valueOf() - dayjs(b.last_sent_date).valueOf()
                  },
                },
                {
                  title: 'Next Scheduled',
                  dataIndex: 'next_scheduled_date',
                  key: 'next_scheduled_date',
                  width: 130,
                  render: (date: string | null) => (
                    <Space>
                      <CalendarOutlined />
                      {date ? (
                        <Text>{dayjs(date).format('MMM DD, YYYY')}</Text>
                      ) : (
                        <Text type="secondary">Complete</Text>
                      )}
                    </Space>
                  ),
                  sorter: (a, b) => {
                    if (!a.next_scheduled_date && !b.next_scheduled_date) return 0
                    if (!a.next_scheduled_date) return 1
                    if (!b.next_scheduled_date) return -1
                    return dayjs(a.next_scheduled_date).valueOf() - dayjs(b.next_scheduled_date).valueOf()
                  },
                },
                {
                  title: 'Status',
                  dataIndex: 'success_status',
                  key: 'success_status',
                  width: 120,
                  render: (status: string | null) => {
                    if (!status) {
                      return <Tag color="default">Pending</Tag>
                    }
                    const color = status.toLowerCase().includes('success') ? 'green' : 
                                  status.toLowerCase().includes('fail') ? 'red' : 
                                  'orange'
                    return (
                      <Tag color={color} icon={color === 'green' ? <CheckCircleOutlined /> : null}>
                        {status}
                      </Tag>
                    )
                  },
                  filters: [
                    { text: 'Pending', value: 'pending' },
                    { text: 'Success', value: 'success' },
                    { text: 'Failed', value: 'fail' },
                  ],
                  onFilter: (value, record) => {
                    if (value === 'pending') return !record.success_status
                    return record.success_status?.toLowerCase().includes(value as string) ?? false
                  },
                },
              ]}
              scroll={{ x: 1100 }}
            />
          </>
        )}
      </Card>

      <Modal
        title="Add New Marketing Schedule Entry"
        open={isModalOpen}
        onCancel={handleModalCancel}
        footer={null}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateEntry}
          preserve={false}
        >
          <Form.Item
            name="sprint_numbers"
            rules={[{ required: true, message: 'Please select at least one sprint number' }]}
            initialValue={[]}
            noStyle
          >
            <Input type="hidden" />
          </Form.Item>

          <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.sprint_numbers !== currentValues.sprint_numbers}>
            {({ getFieldValue, setFieldsValue }) => {
              const selectedValues = getFieldValue('sprint_numbers') || []

              return (
                <div style={{ marginBottom: 24 }}>
                  <div style={{ marginBottom: 8 }}>
                    <Text type="danger">*</Text> <Text>Sprint Numbers</Text>
                  </div>
                  {/* Search Input */}
                  <Input
                    placeholder="Type to search sprint numbers (min 2 characters)..."
                    value={sprintSearchInput}
                    onChange={(e) => setSprintSearchInput(e.target.value)}
                    prefix={<SearchOutlined />}
                    suffix={sprintSearchLoading ? <Spin size="small" /> : null}
                    allowClear
                  />

                  {/* Available Sprint Chips */}
                  {sprintOptions.length > 0 && (
                    <div style={{
                      marginTop: 12,
                      padding: 12,
                      border: '1px solid #d9d9d9',
                      borderRadius: 6,
                      maxHeight: 250,
                      overflowY: 'auto',
                      backgroundColor: '#fafafa'
                    }}>
                      <Space wrap size={[8, 8]}>
                        {sprintOptions.map((option) => {
                          const isSelected = selectedValues.includes(option.value)

                          return (
                            <Tag
                              key={option.value}
                              color={isSelected ? 'blue' : 'default'}
                              style={{
                                cursor: 'pointer',
                                padding: '6px 12px',
                                fontSize: '13px',
                                borderRadius: 16,
                                userSelect: 'none',
                                transition: 'all 0.3s'
                              }}
                              onClick={() => {
                                if (isSelected) {
                                  setFieldsValue({
                                    sprint_numbers: selectedValues.filter((v: string) => v !== option.value)
                                  })
                                } else {
                                  setFieldsValue({
                                    sprint_numbers: [...selectedValues, option.value]
                                  })
                                }
                              }}
                            >
                              {option.label}
                            </Tag>
                          )
                        })}
                      </Space>
                    </div>
                  )}

                  {/* Selected Sprint Chips */}
                  {selectedValues.length > 0 && (
                    <div style={{
                      marginTop: 12,
                      padding: 12,
                      border: '2px solid #1890ff',
                      borderRadius: 6,
                      backgroundColor: '#e6f7ff'
                    }}>
                      <div style={{ marginBottom: 8, fontWeight: 500, color: '#1890ff' }}>
                        Selected ({selectedValues.length})
                      </div>
                      <Space wrap size={[8, 8]}>
                        {selectedValues.map((value: string) => (
                          <Tag
                            key={value}
                            color="blue"
                            closable
                            onClose={(e) => {
                              e.preventDefault()
                              setFieldsValue({
                                sprint_numbers: selectedValues.filter((v: string) => v !== value)
                              })
                            }}
                            style={{
                              padding: '6px 12px',
                              fontSize: '13px',
                              borderRadius: 16
                            }}
                          >
                            {value}
                          </Tag>
                        ))}
                      </Space>
                    </div>
                  )}
                </div>
              )
            }}
          </Form.Item>

          <Form.Item
            name="blacklisted_sprint_numbers"
            initialValue={[]}
            noStyle
          >
            <Input type="hidden" />
          </Form.Item>

          <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.blacklisted_sprint_numbers !== currentValues.blacklisted_sprint_numbers}>
            {({ getFieldValue, setFieldsValue }) => {
              const selectedValues = getFieldValue('blacklisted_sprint_numbers') || []

              return (
                <div style={{ marginBottom: 24 }}>
                  <div style={{ marginBottom: 8 }}>
                    <Text>Blacklisted Sprint Numbers</Text>
                  </div>
                  <Input
                    placeholder="Type to search blacklisted sprint numbers (min 2 characters)..."
                    value={blacklistedSprintSearchInput}
                    onChange={(e) => setBlacklistedSprintSearchInput(e.target.value)}
                    prefix={<SearchOutlined />}
                    suffix={blacklistedSprintSearchLoading ? <Spin size="small" /> : null}
                    allowClear
                  />

                  {blacklistedSprintOptions.length > 0 && (
                    <div style={{
                      marginTop: 12,
                      padding: 12,
                      border: '1px solid #d9d9d9',
                      borderRadius: 6,
                      maxHeight: 250,
                      overflowY: 'auto',
                      backgroundColor: '#fafafa'
                    }}>
                      <Space wrap size={[8, 8]}>
                        {blacklistedSprintOptions.map((option) => {
                          const isSelected = selectedValues.includes(option.value)

                          return (
                            <Tag
                              key={option.value}
                              color={isSelected ? 'red' : 'default'}
                              style={{
                                cursor: 'pointer',
                                padding: '6px 12px',
                                fontSize: '13px',
                                borderRadius: 16,
                                userSelect: 'none',
                                transition: 'all 0.3s'
                              }}
                              onClick={() => {
                                if (isSelected) {
                                  setFieldsValue({
                                    blacklisted_sprint_numbers: selectedValues.filter((v: string) => v !== option.value)
                                  })
                                } else {
                                  setFieldsValue({
                                    blacklisted_sprint_numbers: [...selectedValues, option.value]
                                  })
                                }
                              }}
                            >
                              {option.label}
                            </Tag>
                          )
                        })}
                      </Space>
                    </div>
                  )}

                  {selectedValues.length > 0 && (
                    <div style={{
                      marginTop: 12,
                      padding: 12,
                      border: '2px solid #ff4d4f',
                      borderRadius: 6,
                      backgroundColor: '#fff1f0'
                    }}>
                      <div style={{ marginBottom: 8, fontWeight: 500, color: '#cf1322' }}>
                        Blacklisted ({selectedValues.length})
                      </div>
                      <Space wrap size={[8, 8]}>
                        {selectedValues.map((value: string) => (
                          <Tag
                            key={value}
                            color="red"
                            closable
                            onClose={(e) => {
                              e.preventDefault()
                              setFieldsValue({
                                blacklisted_sprint_numbers: selectedValues.filter((v: string) => v !== value)
                              })
                            }}
                            style={{
                              padding: '6px 12px',
                              fontSize: '13px',
                              borderRadius: 16
                            }}
                          >
                            {value}
                          </Tag>
                        ))}
                      </Space>
                    </div>
                  )}
                </div>
              )
            }}
          </Form.Item>

          {previewData && (
            <Card size="small" style={{ marginBottom: 16 }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Text strong>Sprint {previewData.sprint_number} Preview:</Text>
                <Space wrap>
                  <Tag color="blue">Total: {previewData.total_leads}</Tag>
                  <Tag color="green">Eligible: {previewData.eligible_leads}</Tag>
                  <Tag color="purple">Keywords Generated: {previewData.keywords_generated}</Tag>
                  <Tag color="orange">Reports Generated: {previewData.reports_generated}</Tag>
                </Space>
                <Text type="secondary">{previewData.message}</Text>
              </Space>
            </Card>
          )}

          <Form.Item
            name="batch"
            label="Batch"
            rules={[{ required: true, message: 'Please select a batch type' }]}
          >
            <Select placeholder="Select batch type" onChange={handleBatchChange}>
              <Select.Option value="missed bids">missed bids</Select.Option>
              <Select.Option value="lost bids">lost bids</Select.Option>
              <Select.Option value="3 mail cadence">3 mail cadence</Select.Option>
              <Select.Option value="plus mail cadence">plus mail cadence</Select.Option>
              <Select.Option value="pricing_mail">pricing_mail</Select.Option>
            </Select>
          </Form.Item>

          {selectedBatch && (
            <>
              <Form.Item
                name="date_1"
                label="Campaign Date 1"
                rules={[
                  { required: true, message: 'Please select the first campaign date' },
                  { validator: validateFutureDate }
                ]}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  placeholder="Select date"
                  disabledDate={isScheduleDateDisabled}
                  dateRender={dateRenderWithTooltip}
                />
              </Form.Item>

              {['3 mail cadence', 'plus mail cadence'].includes(selectedBatch) && (
                <>
                  <Form.Item
                    name="date_2"
                    label="Campaign Date 2"
                    rules={[
                      { required: true, message: 'Please select the second campaign date' },
                      { validator: validateFutureDate }
                    ]}
                  >
                    <DatePicker
                      style={{ width: '100%' }}
                      placeholder="Select date"
                      disabledDate={isScheduleDateDisabled}
                      dateRender={dateRenderWithTooltip}
                    />
                  </Form.Item>

                  <Form.Item
                    name="date_3"
                    label="Campaign Date 3"
                    rules={[
                      { required: true, message: 'Please select the third campaign date' },
                      { validator: validateFutureDate }
                    ]}
                  >
                    <DatePicker
                      style={{ width: '100%' }}
                      placeholder="Select date"
                      disabledDate={isScheduleDateDisabled}
                      dateRender={dateRenderWithTooltip}
                    />
                  </Form.Item>
                </>
              )}

              {selectedBatch === 'plus mail cadence' && (
                <>
                  {[4, 5].map(number => (
                    <Form.Item
                      key={number}
                      name={`date_${number}`}
                      label={`Campaign Date ${number}`}
                      rules={[
                        { required: true, message: `Please select campaign date ${number}` },
                        { validator: validateFutureDate }
                      ]}
                    >
                      <DatePicker
                        style={{ width: '100%' }}
                        placeholder="Select date"
                        disabledDate={isScheduleDateDisabled}
                        dateRender={dateRenderWithTooltip}
                      />
                    </Form.Item>
                  ))}
                </>
              )}

              {selectedBatch === 'missed bids' && (
                <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                  Note: "Missed bids" campaigns only require one date.
                </Text>
              )}

              {selectedBatch === 'lost bids' && (
                <>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                    Note: "Lost bids" campaigns only require one date.
                  </Text>
                </>
              )}

              {['lost bids', 'plus mail cadence'].includes(selectedBatch) && (
                  <Form.Item
                    name="include_paying_customers"
                    valuePropName="checked"
                    style={{ marginBottom: 16 }}
                  >
                    <Checkbox>Send to paying customers also</Checkbox>
                  </Form.Item>
              )}

              {selectedBatch === 'pricing_mail' && (
                <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                  Note: "Pricing mail" campaigns only require one date.
                </Text>
              )}
            </>
          )}

          <Text type="secondary" style={{ display: 'block', marginBottom: 16, fontStyle: 'italic' }}>
            Note: Scheduling a sprint automatically inserts the company keys for missed bid and report generation
          </Text>

          <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={handleModalCancel}>
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={creating}
              >
                Create Entry
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Sprint Details Modal */}
      <Modal
        title={
          <Space>
            <BarChartOutlined />
            <Text strong>Sprint Details: {selectedSprint}</Text>
          </Space>
        }
        open={isSprintModalOpen}
        onCancel={handleSprintModalClose}
        footer={null}
        width="90%"
        style={{ maxWidth: 1000 }}
        destroyOnClose
      >
        {sprintDetailsLoading ? (
          <div style={{ textAlign: 'center', padding: '50px 0' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>
              <Text type="secondary">Loading sprint details...</Text>
            </div>
          </div>
        ) : (
          <div>
            <div style={{ marginBottom: 16 }}>
              <Text type="secondary">
                Showing all scheduled campaigns for sprint <Text strong>{selectedSprint}</Text>
              </Text>
            </div>

            <Table
              dataSource={sprintDetails}
              rowKey="id"
              pagination={false}
              size="small"
              columns={[
                {
                  title: 'Campaign ID',
                  dataIndex: 'id',
                  key: 'id',
                  width: 80,
                  render: (id: number) => (
                    <Tag color="blue">#{id}</Tag>
                  ),
                },
                {
                  title: 'Batch Type',
                  dataIndex: 'batch',
                  key: 'batch',
                  width: 140,
                  render: (batch: string) => (
                    <Tag color={['3 mail cadence', 'plus mail cadence'].includes(batch) ? 'purple' : 'orange'}>
                      {batch}
                    </Tag>
                  ),
                },
                {
                  title: 'Leads Count',
                  dataIndex: 'number_of_leads',
                  key: 'number_of_leads',
                  width: 100,
                  render: (count: number | null) => (
                    <Space>
                      <TeamOutlined />
                      <Text strong>{count || 0}</Text>
                    </Space>
                  ),
                },
                {
                  title: 'Scheduled Date',
                  dataIndex: 'scheduled_datetime',
                  key: 'scheduled_datetime',
                  width: 180,
                  render: (date: string) => (
                    <Space>
                      <CalendarOutlined />
                      <Text>{formatDate(date)}</Text>
                    </Space>
                  ),
                  sorter: (a: any, b: any) => dayjs(a.scheduled_datetime).valueOf() - dayjs(b.scheduled_datetime).valueOf(),
                  defaultSortOrder: 'descend' as const,
                },
                {
                  title: 'Campaign Dates',
                  key: 'campaign_dates',
                  render: (record: MarketingSchedule) => {
                    const now = dayjs()
                    
                    const renderDate = (date: string | null, label: string) => {
                      if (!date) return null
                      const dateObj = dayjs(date)
                      const isPast = dateObj.isBefore(now)
                      const isToday = dateObj.isSame(now, 'day')
                      
                      let color = 'default'
                      if (isPast) color = 'success'
                      else if (isToday) color = 'warning'
                      else color = 'processing'
                      
                      return (
                        <div key={label} style={{ marginBottom: 4 }}>
                          <Tag color={color} style={{ fontSize: '12px' }}>
                            {label}: {dateObj.format('MMM DD, YYYY HH:mm')}
                          </Tag>
                        </div>
                      )
                    }
                    
                    return (
                      <div>
                        {renderDate(record.date_1, 'Date 1')}
                        {renderDate(record.date_2, 'Date 2')}
                        {renderDate(record.date_3, 'Date 3')}
                        {renderDate(record.date_4, 'Date 4')}
                        {renderDate(record.date_5, 'Date 5')}
                      </div>
                    )
                  },
                },
                {
                  title: 'Status',
                  dataIndex: 'success_status',
                  key: 'success_status',
                  width: 120,
                  render: (status: string | null) => {
                    if (!status) {
                      return <Tag color="default">Pending</Tag>
                    }
                    const color = status.toLowerCase().includes('success') ? 'green' : 
                                  status.toLowerCase().includes('fail') ? 'red' : 
                                  'orange'
                    return (
                      <Tag color={color} icon={color === 'green' ? <CheckCircleOutlined /> : null}>
                        {status}
                      </Tag>
                    )
                  },
                },
              ]}
              scroll={{ y: 400 }}
              bordered
            />

            {sprintDetails.length === 0 && (
              <div style={{ textAlign: 'center', padding: '50px 0' }}>
                <Text type="secondary">No campaigns found for this sprint.</Text>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Email Analytics Section */}
      <div ref={emailAnalyticsRef}>
        <Card
          title={
            <Space wrap>
              <MailOutlined />
              <Title level={4} style={{ margin: 0, fontSize: 'clamp(16px, 4vw, 20px)' }}>Email Analytics</Title>
              <Text type="secondary" style={{ fontSize: 'clamp(12px, 3vw, 14px)' }}>Daily email event tracking</Text>
            </Space>
          }
          style={{ marginTop: 16 }}
        >
        <div style={{ marginBottom: 24 }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text>Select Campaign Date:</Text>
                <DatePicker
                  value={selectedEmailDate}
                  onChange={handleEmailDateChange}
                  placeholder="Select a campaign date"
                  style={{ width: '100%', maxWidth: 300 }}
                  disabledDate={(current) => current && current >= dayjs().startOf('day')}
                />
              </Space>
            </Col>
            <Col xs={24} md={12}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text>Sprint Number:</Text>
                <Space.Compact style={{ width: '100%', maxWidth: 300 }}>
                  <Select
                    value={selectedEmailSprint || undefined}
                    onChange={setSelectedEmailSprint}
                    placeholder="Select a sprint"
                    style={{ flex: 1 }}
                    allowClear
                    loading={sprintsLoading}
                    disabled={!selectedEmailDate || availableSprints.length === 0}
                    notFoundContent={sprintsLoading ? 'Loading...' : availableSprints.length === 0 ? 'No sprints available for this date' : 'Not found'}
                  >
                    {availableSprints.map(sprint => (
                      <Select.Option key={sprint} value={sprint}>
                        {sprint}
                      </Select.Option>
                    ))}
                  </Select>
                  {selectedEmailDate && selectedEmailSprint && (
                    <Button
                      icon={<SearchOutlined />}
                      onClick={handleEmailSearch}
                      loading={emailAnalyticsLoading}
                      type="primary"
                    >
                      Search
                    </Button>
                  )}
                </Space.Compact>
              </Space>
            </Col>
          </Row>
        </div>

        {(!selectedEmailDate || !selectedEmailSprint) && (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <MailOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />
            <div style={{ marginTop: 16 }}>
              <Text type="secondary">Please select both a date and sprint number to view email analytics</Text>
            </div>
          </div>
        )}

        {emailAnalyticsLoading && selectedEmailDate && (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>
              <Text type="secondary">Loading email analytics...</Text>
            </div>
          </div>
        )}

        {emailAnalytics && !emailAnalyticsLoading && (
          <>
            {/* Email Analytics Results - 4 Key Metrics */}
            {emailAnalytics.aggregate_stats && (
              <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} md={6}>
                  <Card size="small">
                    <Statistic
                      title="Number of Leads"
                      value={emailAnalytics.aggregate_stats.total_sprint_leads || 0}
                      prefix={<TeamOutlined />}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Card size="small">
                    <Statistic
                      title="Number of Emails"
                      value={emailAnalytics.aggregate_stats.emails_delivered || 0}
                      prefix={<MailOutlined />}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Card size="small">
                    <Statistic
                      title="Unique Lead Clicks"
                      value={emailAnalytics.aggregate_stats.unique_clicks_by_lead || 0}
                      prefix={<TeamOutlined />}
                      valueStyle={{ color: '#1890ff' }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Card size="small">
                    <Statistic
                      title="Unique Email Clicks"
                      value={emailAnalytics.aggregate_stats.unique_clicks_by_email || 0}
                      prefix={<MailOutlined />}
                      valueStyle={{ color: '#1890ff' }}
                    />
                  </Card>
                </Col>
              </Row>
            )}

          </>
        )}
      </Card>
      </div>
    </div>
  )
}

export default Marketing
